import {
  FinnairBoundFareFamily,
  FinnairCart,
  FinnairItineraryItemFlight,
  FinnairPassengerCode,
  FinnairPassengerItem,
  FinnairPricePerPassenger,
  FinnairServiceItem,
} from '@fcom/dapi/api/models';

// Names the route that a per-flight block of a cart bound applies to, e.g. "Helsinki–New York". Falls back to
// the location codes while the city-name lookup is still empty.
export const routeLabel = (flight: FinnairItineraryItemFlight, cityNames: Record<string, string>): string => {
  const departure = cityNames?.[flight.departure.locationCode] ?? flight.departure.locationCode;
  const arrival = cityNames?.[flight.arrival.locationCode] ?? flight.arrival.locationCode;
  return `${departure}–${arrival}`;
};

export const priceForPassengerTypeCode = (
  boundFareFamily: FinnairBoundFareFamily,
  cartPassengerTypeCode: FinnairPassengerCode
): FinnairPricePerPassenger | undefined =>
  boundFareFamily?.pricePerPassengers?.find((pricePerPax) =>
    pricePerPax.passengerTypeCodes.some((passengerTypeCode) => passengerTypeCode === cartPassengerTypeCode)
  );

export const getPassengerAndInfantIds = (cart: FinnairCart, passengerId: string): string[] => {
  const infant = cart.passengers?.find(({ accompanyingTravelerId }) => accompanyingTravelerId === passengerId);
  return infant ? [passengerId, infant.id] : [passengerId];
};

export const filterServiceItemByPassenger = (
  item: FinnairServiceItem,
  passengerId: string | null,
  cartPassengers: FinnairPassengerItem[]
): FinnairServiceItem => {
  if (!passengerId) {
    return item;
  }
  const infant = cartPassengers.find((pax) => pax.accompanyingTravelerId === passengerId);
  return {
    ...item,
    bounds: item.bounds.map((boundItem) => ({
      ...boundItem,
      segments: boundItem.segments.map((segment) => ({
        ...segment,
        passengers: segment.passengers.filter(({ id }) => id === passengerId || id === infant?.id),
      })),
    })),
  };
};

/**
 * Narrows a service item to one flight of the journey, keeping the bound wrappers so the per-bound summary
 * utils can be asked the same question per flight.
 */
export const filterServiceItemByFlight = (item: FinnairServiceItem, flightId: string): FinnairServiceItem => ({
  ...item,
  bounds: item.bounds.map((boundItem) => ({
    ...boundItem,
    segments: boundItem.segments.filter((segment) => segment.id === flightId),
  })),
});

const BENEFIT_LABEL_REGEX = /^(\d+)(\s*x?\s*)/i;

export const multiplyLabelAmount = (label: string, passengerCount: number): string => {
  const match = label.match(BENEFIT_LABEL_REGEX);
  if (match) {
    const originalNumber = parseInt(match[1], 10);
    const multipliedNumber = originalNumber * passengerCount;
    return label.replace(/^\d+/, String(multipliedNumber));
  }
  return label;
};
